import React, { useState, Fragment, useEffect, useRef } from 'react'
import { graphql } from 'gatsby'
import { Helmet } from 'react-helmet'
import Fuse from 'fuse.js'
import TutorialTiles from './components/TutorialTiles'
import { FaSearch } from 'react-icons/fa'
import FilterComponent from './components/filters'
import HeroBanner from './components/HeroBanner'
import LearnMoreComponent from './components/LearnMore'
import {
  getQueryParams,
  useQueryParamString,
} from 'react-use-query-param-string'
import { shuffleArray } from '../utils/shuffleArray'

const Tutorials = ({ data }) => {
  const [, setSelectedLanguagesParam] = useQueryParamString('languages', '')
  const [selectedLanguages, setSelectedLanguages] = useState(
    Array.isArray(getQueryParams().languages)
      ? getQueryParams().languages
      : getQueryParams().languages
      ? [getQueryParams().languages]
      : []
  )

  const [, setSearchParam] = useQueryParamString('search', '')
  const [input, setInput] = useState(getQueryParams().search || '')
  const [, setSelectedTagsParam] = useQueryParamString('tags', '')
  const [selectedTags, setSelectedTags] = useState(
    Array.isArray(getQueryParams().tags)
      ? getQueryParams().tags
      : getQueryParams().tags
      ? [getQueryParams().tags]
      : []
  )
  const [selectedTechnology, setSelectedTechnology] = useQueryParamString(
    'technology',
    ''
  )
  const [selectedContentType, setSelectedContentType] = useQueryParamString(
    'type',
    ''
  )
  const tutorialsWithTrailingSlash = data.tutorials.nodes.map((tutorial) => ({
    ...tutorial,
    frontmatter: {
      ...tutorial.frontmatter,
      path: tutorial.frontmatter.path.endsWith('/')
        ? tutorial.frontmatter.path
        : `${tutorial.frontmatter.path}/`,
    },
  }))

  const [allTutorials, setAllTutorials] = useState(tutorialsWithTrailingSlash)

  const [currentPage, setCurrentPage] = useState(1)
  const tutorialsPerPage = 12

  const searchInputRef = useRef(null)
  const searchSectionRef = useRef(null)

  useEffect(() => {
    setAllTutorials(shuffleArray([...data.tutorials.nodes]))
    const hasUrlParams = Object.keys(getQueryParams()).length > 0
    if (hasUrlParams) {
      searchInputRef.current?.focus()
      searchSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [])

  const handleSelectedLanguages = (v) => {
    setSelectedLanguages(v)
    setSelectedLanguagesParam(v)
  }

  const onSelectContentType = (_value) => {
    setSelectedContentType(_value)
  }

  // Handle input change with URL param update
  const handleInputChange = (event) => {
    const value = event.target.value
    setInput(value)
    setSearchParam(value)
  }

  const fuseOptions = {
    keys: [
      'frontmatter.title',
      'frontmatter.short_title',
      'frontmatter.description',
      'frontmatter.tags',
      'frontmatter.technology',
      'frontmatter.sdk_language'
    ],
    threshold: 0.2,
    ignoreLocation: true,  // Don't penalize matches based on their location in the text
    useExtendedSearch: true,  // Enable extended search features
    shouldSort: true,  // Sort results by score
  }

  useEffect(() => { 
    const fuse = new Fuse([...data.tutorials.nodes], fuseOptions)
    const filteredTutorials = input
    ? fuse.search(input).map(result => result.item)
    : shuffleArray([...data.tutorials.nodes])
    setAllTutorials(filteredTutorials)
  }, [input, data.tutorials.nodes])

  let filteredContent = allTutorials

  if (selectedLanguages && selectedLanguages.length) {
    filteredContent = filteredContent.filter((content_item) => {
      if (content_item.frontmatter.sdk_language !== null) {
        return (
          content_item.frontmatter.sdk_language.filter((value) =>
            selectedLanguages.some((lang) => value.includes(lang))
          ).length > 0
        )
      }
      return ''
    })
  }

  if (selectedTags.length > 0) {
    filteredContent = filteredContent.filter((content_item) => {
      if (content_item.frontmatter.tags !== null) {
        return (
          content_item.frontmatter.tags.filter((value) =>
            selectedTags.includes(value)
          ).length > 0
        )
      }
      return ''
    })
  }

  let tech = filteredContent.map((content) => content.frontmatter.technology)
  const contentLength = filteredContent.length

  if (selectedTechnology.length > 0) {
    filteredContent = filteredContent.filter((content_item) => {
      if (content_item.frontmatter.technology !== null) {
        return (
          content_item.frontmatter.technology.filter((value) =>
            selectedTechnology.includes(value)
          ).length > 0
        )
      }
      return ''
    })
  }

  const tutorials = filteredContent.filter(
    (content_item) => content_item.frontmatter.content_type === 'tutorial'
  )
  const learningPaths = filteredContent.filter(
    (content_item) => content_item.frontmatter.content_type === 'learn'
  )
  const quickstarts = filteredContent.filter(
    (content_item) => content_item.frontmatter.content_type === 'quickstart'
  )

  let searchedContent = [...filteredContent]
  if (selectedContentType === 'Quickstart Guides') {
    searchedContent = quickstarts
  }

  if (selectedContentType === 'Tutorials') {
    searchedContent = tutorials
  }

  if (selectedContentType === 'Learning Paths') {
    searchedContent = learningPaths
  }

  const clearSearch = () => {
    setInput('')
    setSearchParam('')

    setSelectedLanguages([])
    setSelectedLanguagesParam('')

    setSelectedTags([])
    setSelectedTagsParam('')

    setSelectedTechnology('')
    setSelectedContentType('')
  }

  const [aiTutorialsMemo, setAiTutorialsMemo] = useState([])

  useEffect(() => {
    // Only set AI tutorials once on initial load
    if (aiTutorialsMemo.length === 0) {
      // Get AI Tutorials
      let allTutorialsFrontmatter = [...tutorialsWithTrailingSlash].map((node) => node.frontmatter)
      const aiTutorials = allTutorialsFrontmatter.filter((tutorial) =>
        tutorial.tags.includes('Artificial Intelligence')
      )

      // randomize the order of the AI tutorials and keep only 6
      const shuffledAiTutorials = [...aiTutorials].sort(() => Math.random() - 0.5).slice(0, 6)
      setAiTutorialsMemo(shuffledAiTutorials)
    }
  }, [tutorialsWithTrailingSlash, aiTutorialsMemo.length])

  // Pagination
  // Calculate indexes
  const indexOfLastTutorial = currentPage * tutorialsPerPage
  const indexOfFirstTutorial = indexOfLastTutorial - tutorialsPerPage
  const currentContent = searchedContent.slice(
    indexOfFirstTutorial,
    indexOfLastTutorial
  )

  // Calculate total pages
  const totalPages = Math.ceil(searchedContent.length / tutorialsPerPage)

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1)
    }
  }, [currentPage, totalPages])

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber)
    document
      .getElementById('cb-tutorials-container-header')
      .scrollIntoView({ behavior: 'smooth' })
  }

  // Previous page
  const previousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
      document
        .getElementById('cb-tutorials-container-header')
        .scrollIntoView({ behavior: 'smooth' })
    }
  }

  // Next page
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
      document
        .getElementById('cb-tutorials-container-header')
        .scrollIntoView({ behavior: 'smooth' })
    }
  }

  const allFilters = [input, selectedContentType, ...selectedLanguages, ...selectedTags, selectedTechnology]


  return (
    <Fragment>
      <HeroBanner tutorials={aiTutorialsMemo} />
      <div className="page--tutorials wrapper mb-24 px-7" ref={searchSectionRef}>
        <Helmet title={`Tutorials | Couchbase Developer Portal`}>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, shrink-to-fit=no user-scalable=no"
          />
        </Helmet>
        <div
          className="pt-4 xl:pt-8 pb-12 text-center wrapper"
          id="cb-tutorials-container-header"
        >
          <h2>Learn with Couchbase Tutorials</h2>
          <p>
            The tutorials and learning paths available cover a wide range of
            topics related to Couchbase. These topics include CRUD operations,
            SQL querying, transactions, building REST APIs, and more.
          </p>
        </div>
        <div className="search-wrapper">
          <input
            className="search-input"
            placeholder="Search..."
            name="input"
            value={input}
            onChange={handleInputChange}
          />
          <FaSearch className="search-icon" />
        </div>
        <div className="filter-accordions flex justify-between flex-wrap">
          <FilterComponent
            selectedLanguages={selectedLanguages}
            setSelectedLanguages={handleSelectedLanguages}
            selectedTags={selectedTags}
            setSelectedTags={setSelectedTagsParam}
            selectedTechnology={selectedTechnology}
            setSelectedTechnology={setSelectedTechnology}
            selectedContentType={selectedContentType}
            onSelectContentType={onSelectContentType}
            tech={tech}
            contentLength={contentLength}
            tutorials={tutorials}
            learningPaths={learningPaths}
            quickstarts={quickstarts}
          />
        </div>
        {input ||
        selectedLanguages.length ||
        selectedTags.length ||
        selectedTechnology.length ||
        selectedContentType.length ? (
          <div className="w-full lg:w-4/5 relative flex flex-wrap">
            {searchedContent.length} Results filtered by: 
            <div className='filter-tag-list inline'>
              {allFilters.map((filter) => {
                if (filter.length > 0) {
                  return (
                    <span key={filter} className="filter-tag mx-1">
                      {filter}
                    </span>
                  )
                }
                return ''
              })}
            </div>
            <div className="clear-tags" onClick={clearSearch}>
              Clear all filters
            </div>
          </div>
        ) : (
          ''
        )}
        <div className="flex flex-row" ref={searchInputRef}>
          <div className="w-full">
            {searchedContent.length === 0 ? (
              <p className="pt-8 pb-20">
                There are no results found in the category
              </p>
            ) : (
              ''
            )}
            {searchedContent.length > 0 && (
              <Fragment>
                {currentContent.length ? (
                  <Fragment>
                    {/* <div className="tutorial-heading mt-10 mb-10">
                      <div
                        className="text-2xl font-semibold"
                        style={{ display: 'inline-block' }}
                        id="tutorials-heading"
                      >
                        Tutorials
                      </div>
                    </div> */}
                    <div className="tutorial-section">
                      <div className={'grid-tiles-container-tutorials'}>
                        {currentContent.map((tutorial, idx) => {
                          const tutorialWithTrailingSlash = tutorialsWithTrailingSlash.find(
                            (t) => t.id === tutorial.id
                          )
                          return <TutorialTiles key={tutorial.id} tutorial={tutorialWithTrailingSlash || tutorial} idx={currentPage* tutorialsPerPage + idx}/>
                        })}
                      </div>
                    </div>

                    <div className="pagination">
                      <button
                        onClick={previousPage}
                        disabled={currentPage === 1}
                        className={`pagination-button ${
                          currentPage === 1 ? 'hidden' : ''
                        }`}
                      >
                        &lt; Previous
                      </button>

                      {totalPages > 3 ? (
                        <>
                          {currentPage > 2 && (
                            <button
                              key={1}
                              onClick={() => paginate(1)}
                              className={`pagination-button ${
                                currentPage === 1 ? 'active' : ''
                              }`}
                            >
                              1
                            </button>
                          )}
                          {currentPage > 2 && (
                            <span className="pagination-ellipsis">...</span>
                          )}
                          {[...Array(totalPages)].map((_, index) => {
                            if (
                              index + 1 === currentPage ||
                              index + 1 === currentPage - 1 ||
                              index + 1 === currentPage + 1
                            ) {
                              return (
                                <button
                                  key={index + 1}
                                  onClick={() => paginate(index + 1)}
                                  className={`pagination-button ${
                                    currentPage === index + 1 ? 'active' : ''
                                  }`}
                                >
                                  {index + 1}
                                </button>
                              )
                            }
                            return null
                          })}
                          {currentPage < totalPages - 1 && (
                            <span className="pagination-ellipsis">...</span>
                          )}
                          {currentPage < totalPages - 1 && (
                            <button
                              key={totalPages}
                              onClick={() => paginate(totalPages)}
                              className={`pagination-button ${
                                currentPage === totalPages ? 'active' : ''
                              }`}
                            >
                              {totalPages}
                            </button>
                          )}
                        </>
                      ) : (
                        [...Array(totalPages)].map((_, index) => (
                          <button
                            key={index + 1}
                            onClick={() => paginate(index + 1)}
                            className={`pagination-button ${
                              currentPage === index + 1 ? 'active' : ''
                            }`}
                          >
                            {index + 1}
                          </button>
                        ))
                      )}

                      <button
                        onClick={nextPage}
                        disabled={currentPage === totalPages}
                        className={`pagination-button ${
                          currentPage === totalPages ? 'hidden' : ''
                        }`}
                      >
                        Next &gt;
                      </button>
                    </div>
                  </Fragment>
                ) : (
                  ''
                )}
              </Fragment>
            )}
          </div>
        </div>
      </div>
      <LearnMoreComponent />
    </Fragment>
  )
}

export default Tutorials

// Video on using gatsby-plugin-image: https://www.youtube.com/watch?v=zRtFwzF4p1o
// import style module to style this page
// sorting: https://www.youtube.com/watch?v=G3KkHk8iSOg
// multi querys: https://www.youtube.com/watch?v=WmSJMj-gNKo

export const query = graphql`
  query tutorialsQuery {
    tutorials: allMarkdownRemark(
      filter: {
        frontmatter: {
          content_type: { in: ["tutorial", "learn", "quickstart"] }
          exclude_tutorials: { in: [null, false] }
        }
      }
    ) {
      nodes {
        frontmatter {
          path
          content_type
          title
          short_title
          technology
          filter
          description
          length
          tags
          sdk_language
          exclude_tutorials
        }
        id
      }
    }
  }
`
